import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { GripHorizontal, X } from 'lucide-react';
import MarkdownEditor from '../MarkdownEditor/MarkdownEditor';
import useDraggable, { PanelGeometry } from '../../../core/hooks/useDraggable';
import { SharedText } from '../../../sdk/@types/Acertei';

export interface SharedTextPanelProps {
  text: SharedText;
  /** "questão 2 de 4" — omitido quando o texto tem uma questão só. */
  groupPosition?: { index: number; total: number };
  onClose: () => void;
  /** Abaixo de 768px vira folha inferior, sem arrasto. */
  mobile?: boolean;
  /** Persiste posição/tamanho entre aberturas. */
  geometryRef?: React.MutableRefObject<PanelGeometry | null>;
}

/**
 * Leitor flutuante do texto base.
 *
 * Não bloqueante de propósito: sem máscara e sem `pointer-events` por cima da
 * página, o aluno lê o texto e responde a questão ao mesmo tempo — que é a
 * diferença entre isto e um `Modal` do antd.
 *
 * Renderizado por portal no `document.body` porque o carrossel vive dentro de
 * um flex com `height: calc(100vh - 80px)` e `overflow-y: auto`: um filho
 * `position: fixed` ali dentro herdaria o bloco de contenção errado.
 */
export default function SharedTextPanel({
  text,
  groupPosition,
  onClose,
  mobile = false,
  geometryRef,
}: SharedTextPanelProps) {
  const { geometry, dragHandleProps, resizeHandleProps } = useDraggable({
    geometryRef,
    enabled: !mobile,
  });

  // Esc também fecha — segunda saída, caso algum handler engula o clique no X.
  useEffect(() => {
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const header = (
    <div
      {...(mobile ? {} : dragHandleProps)}
      className={`flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2 dark:border-[#1a2e1c] ${
        mobile ? '' : 'cursor-move select-none touch-none'
      }`}
      style={mobile ? undefined : { touchAction: 'none' }}
    >
      <div className="flex min-w-0 items-center gap-2">
        {!mobile && <GripHorizontal size={16} className="shrink-0 text-gray-400" />}
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {text.title}
          </p>
          {(text.source || groupPosition) && (
            <p className="truncate text-xs text-gray-500 dark:text-[#6b8f6e]">
              {groupPosition && `Questão ${groupPosition.index} de ${groupPosition.total}`}
              {groupPosition && text.source && ' · '}
              {text.source}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fechar texto"
        className="shrink-0 rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-[#6b8f6e] dark:hover:bg-[#1a2e1c] dark:hover:text-white"
      >
        <X size={16} />
      </button>
    </div>
  );

  const body = (
    <div className="flex-1 overflow-y-auto p-3">
      <MarkdownEditor readOnly value={text.body} />
    </div>
  );

  if (mobile) {
    // Tela cheia no celular: 70vh deixava o leitor espremido e a página de
    // trás "vazando" — em telas pequenas não há espaço útil para as duas
    // coisas ao mesmo tempo. 100dvh (e não 100vh) acompanha a barra de
    // endereço retrátil dos navegadores móveis; o padding inferior respeita
    // o safe-area de aparelhos com gesto/notch.
    return createPortal(
      <div
        data-shared-text-panel
        className="fixed inset-0 z-[1000] flex h-[100dvh] w-full flex-col bg-white dark:bg-[#0f1a10]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {header}
        {body}
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      data-shared-text-panel
      className="fixed z-[1000] flex flex-col rounded-lg border border-gray-200 bg-white shadow-2xl dark:border-[#1a2e1c] dark:bg-[#0f1a10]"
      style={{
        left: geometry.x,
        top: geometry.y,
        width: geometry.w,
        height: geometry.h,
      }}
    >
      {header}
      {body}
      <div
        {...resizeHandleProps}
        aria-hidden
        className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize touch-none"
        style={{ touchAction: 'none' }}
      >
        <span className="absolute bottom-1 right-1 block h-2 w-2 border-b-2 border-r-2 border-gray-400 dark:border-[#3a5a3c]" />
      </div>
    </div>,
    document.body
  );
}
